/**
 * Live decline → hedge → answer band for the Mirror Guardian page (§2 / mockup).
 *
 * A single 0..1 evidence axis split into three zones by the two thresholds:
 *   [0, decline)      DECLINE — the Mirror declines to answer (burgundy)
 *   [decline, hedge)  HEDGE   — the Mirror answers, but hedges (gold)
 *   [hedge, 1]        ANSWER  — the Mirror answers directly (green)
 *
 * Purely presentational: it re-renders as the sliders move so the admin can
 * see, in real time, how much of the evidence range maps to each behaviour.
 * When decline > hedge (a non-monotonic draft) the band is drawn in an invalid
 * state so the misconfiguration is visible before the client-side guard blocks
 * the save.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, typography } from '../../../theme/designTokens';

export interface GuardianBandVisualizationProps {
  /** evidence_floor_decline, 0..1 */
  decline: number;
  /** evidence_band_hedge, 0..1 */
  hedge: number;
}

const clampUnit = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
const pct = (n: number) => `${(clampUnit(n) * 100).toFixed(0)}%`;

const GuardianBandVisualization: React.FC<GuardianBandVisualizationProps> = ({ decline, hedge }) => {
  const d = clampUnit(decline);
  const h = clampUnit(hedge);
  const invalid = d > h;

  // Widths of the three zones as a fraction of the axis. In the invalid
  // (decline > hedge) case the hedge zone collapses to zero and the overlap is
  // shown as a hatched conflict band so the problem is obvious on screen.
  const declineW = Math.min(d, h);
  const hedgeW = Math.max(0, h - d);
  const answerW = 1 - Math.max(d, h);
  const conflictW = invalid ? d - h : 0;

  return (
    <Box>
      {/* Zone legend */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <LegendSwatch color={colors.error.main} label="Decline" caption="Not enough evidence" />
        <LegendSwatch color={colors.warning.main} label="Hedge" caption="Answer with caveats" />
        <LegendSwatch color={colors.success.main} label="Answer" caption="Answer directly" />
      </Box>

      {/* The band */}
      <Box
        role="img"
        aria-label={`Evidence band: decline below ${pct(d)}, hedge from ${pct(d)} to ${pct(
          h
        )}, answer above ${pct(h)}`}
        sx={{
          position: 'relative',
          height: 44,
          borderRadius: '10px',
          overflow: 'hidden',
          display: 'flex',
          border: `1px solid ${colors.gray[200]}`,
        }}
      >
        <Zone width={declineW} color={colors.error.main} />
        {conflictW > 0 ? (
          <Zone
            width={conflictW}
            color={colors.error.dark}
            hatched
          />
        ) : (
          <Zone width={hedgeW} color={colors.warning.main} />
        )}
        <Zone width={answerW} color={colors.success.main} />
      </Box>

      {/* Threshold markers under the axis */}
      <Box sx={{ position: 'relative', height: 26, mt: 0.5 }}>
        <ThresholdMarker position={d} label="decline" value={pct(d)} invalid={invalid} />
        <ThresholdMarker position={h} label="hedge" value={pct(h)} invalid={invalid} />
      </Box>

      {invalid && (
        <Typography
          variant="caption"
          sx={{ color: colors.error.main, fontWeight: 600, display: 'block', mt: 0.5 }}
        >
          Decline floor is above the hedge threshold — the Mirror would have no hedge zone. Fix
          before saving.
        </Typography>
      )}
    </Box>
  );
};

const Zone: React.FC<{ width: number; color: string; hatched?: boolean }> = ({
  width,
  color,
  hatched,
}) => (
  <Box
    sx={{
      width: `${clampUnit(width) * 100}%`,
      backgroundColor: color,
      backgroundImage: hatched
        ? 'repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0 6px, transparent 6px 12px)'
        : 'none',
      transition: 'width 120ms ease-out',
    }}
  />
);

const ThresholdMarker: React.FC<{
  position: number;
  label: string;
  value: string;
  invalid?: boolean;
}> = ({ position, label, value, invalid }) => (
  <Box
    sx={{
      position: 'absolute',
      left: `${clampUnit(position) * 100}%`,
      transform: 'translateX(-50%)',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontFamily: typography.fonts.mono,
        fontWeight: 700,
        fontSize: '11px',
        color: invalid ? colors.error.main : colors.gray[700],
        display: 'block',
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{ fontSize: '10px', color: colors.gray[600], textTransform: 'uppercase', letterSpacing: '0.5px' }}
    >
      {label}
    </Typography>
  </Box>
);

const LegendSwatch: React.FC<{ color: string; label: string; caption: string }> = ({
  color,
  label,
  caption,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: color, flexShrink: 0 }} />
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary' }}>
        {caption}
      </Typography>
    </Box>
  </Box>
);

export default GuardianBandVisualization;
